import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { formatPaymentMode } from '../../lib/utils';

const CustomerSection = ({
  channel, setChannel,
  customerRef, customerSearch, setCustomerSearch,
  customerDropdownOpen, setCustomerDropdownOpen,
  filteredCustomers, selectCustomer, setSelectedCustomer,
  selectedAgentId, setSelectedAgentId, agents,
  selectedSalesmanId, setSelectedSalesmanId, salesmen,
  billType, setBillType,
  invoiceNumber, setInvoiceNumber,
  transportName, setTransportName,
  seNotes, setSeNotes,
  custObj, customerDiscount, applyDiscount, setApplyDiscount,
  // Distributor mode props
  distributorRef, distributorSearch, setDistributorSearch,
  distributorDropdownOpen, setDistributorDropdownOpen,
  filteredDistributors, selectDistributor, setSelectedDistributor,
  selectedDistributor, currentBoxNumber, currentDistCustId, currentDistCustName,
  needsCustPick, distCustRef,
  distCustSearch, setDistCustSearch,
  distCustDropdownOpen, setDistCustDropdownOpen,
  filteredDistCusts, selectDistCustomer,
  rows,
}) => {
  return (
    <Card>
      <CardContent className="p-4">
        {channel !== 'via_distributor' ? (
          <>
            {/* Direct / Via Agent Mode */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative" ref={customerRef}>
                <Label className="text-xs font-semibold text-gray-600">Customer *</Label>
                <Input value={customerSearch}
                  onChange={e => { setCustomerSearch(e.target.value); setCustomerDropdownOpen(!!e.target.value.trim()); if (!e.target.value) setSelectedCustomer(''); }}
                  placeholder="Search customer..." className="h-9 text-sm" data-testid="se-customer-search"
                />
                {customerDropdownOpen && filteredCustomers.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCustomers.map(c => (
                      <div key={c.id} className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm"
                        onClick={() => selectCustomer(c)} data-testid={`se-cust-option-${c.id}`}>
                        <span className="font-medium">{c.company_name}</span>
                        {c.city && <span className="text-gray-400 ml-2">({c.city})</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Channel</Label>
                <select value={channel} onChange={e => setChannel(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="se-channel">
                  <option value="direct">Direct</option>
                  <option value="via_agent">Via Agent</option>
                  <option value="via_distributor">Via Distributor</option>
                </select>
              </div>
              {channel === 'via_agent' && (
                <div>
                  <Label className="text-xs font-semibold text-gray-600">Agent *</Label>
                  <select value={selectedAgentId} onChange={e => setSelectedAgentId(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                    data-testid="se-agent-select">
                    <option value="">Select Agent</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold text-gray-600">Sales Person *</Label>
                <select value={selectedSalesmanId} onChange={e => setSelectedSalesmanId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                  data-testid="se-salesman-select" required>
                  <option value="">Select Sales Person</option>
                  {salesmen.map(s => <option key={s.id} value={s.id}>{s.full_name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Bill Type</Label>
                <select value={billType} onChange={e => setBillType(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm"
                  data-testid="se-bill-type">
                  <option value="invoice">Invoice</option>
                  <option value="challan">Challan</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Invoice Number</Label>
                <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-001" className="h-9 text-sm" data-testid="se-invoice" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Transport</Label>
                <Input value={transportName} onChange={e => setTransportName(e.target.value)} placeholder="Transport" className="h-9 text-sm" data-testid="se-transport" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Notes</Label>
                <Input value={seNotes} onChange={e => setSeNotes(e.target.value)} placeholder="Entry notes..." className="h-9 text-sm" data-testid="se-notes" />
              </div>
            </div>
            {/* Discount prompt */}
            {custObj && customerDiscount > 0 && applyDiscount === null && (
              <div className="mt-3 p-3 border border-green-200 rounded-lg bg-green-50 flex items-center gap-3" data-testid="se-discount-prompt">
                <span className="text-sm text-green-800">Customer <strong>{custObj.company_name}</strong> has <strong>{customerDiscount}%</strong> discount. Apply to this entry?</span>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" onClick={() => setApplyDiscount(true)} data-testid="se-apply-discount-yes">Yes, Apply</Button>
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setApplyDiscount(false)} data-testid="se-apply-discount-no">No</Button>
              </div>
            )}
            {custObj && (
              <div className="mt-2 p-3 rounded-lg bg-slate-50 border border-slate-200 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 text-xs" data-testid="se-customer-summary">
                <div><span className="text-gray-400 block">Channel</span><strong className="text-gray-700 capitalize">{channel.replace(/_/g, ' ')}</strong></div>
                <div><span className="text-gray-400 block">WSP Type</span><strong className="text-gray-700 capitalize">{custObj.wsp_type || 'Local'}</strong></div>
                {custObj.discount_percent > 0 && <div><span className="text-gray-400 block">Discount</span><strong className="text-green-700">{custObj.discount_percent}%</strong> {applyDiscount === true ? '(Applied)' : applyDiscount === false ? '(Not applied)' : ''}</div>}
                <div><span className="text-gray-400 block">Payment Mode</span><strong className="text-gray-700">{custObj.payment_mode ? formatPaymentMode(custObj.payment_mode) : '—'}</strong></div>
                <div><span className="text-gray-400 block">Transport</span><strong className="text-gray-700">{custObj.transport_preference || '—'}</strong></div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Distribution Mode */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative" ref={distributorRef}>
                <Label className="text-xs font-semibold text-gray-600">Distributor *</Label>
                <Input value={distributorSearch}
                  onChange={e => { setDistributorSearch(e.target.value); setDistributorDropdownOpen(!!e.target.value.trim()); if (!e.target.value) setSelectedDistributor(''); }}
                  placeholder="Search distributor..." className="h-9 text-sm" data-testid="se-distributor-search"
                />
                {distributorDropdownOpen && filteredDistributors.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredDistributors.map(d => (
                      <div key={d.id} className="px-3 py-2 hover:bg-emerald-50 cursor-pointer text-sm"
                        onClick={() => selectDistributor(d)} data-testid={`se-dist-option-${d.id}`}>
                        <span className="font-medium">{d.company_name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Channel</Label>
                <select value={channel} onChange={e => setChannel(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-2 py-1 text-sm" data-testid="se-channel">
                  <option value="direct">Direct</option>
                  <option value="via_agent">Via Agent</option>
                  <option value="via_distributor">Via Distributor</option>
                </select>
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Invoice Number</Label>
                <Input value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} placeholder="INV-001" className="h-9 text-sm" data-testid="se-invoice" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Transport</Label>
                <Input value={transportName} onChange={e => setTransportName(e.target.value)} placeholder="Transport" className="h-9 text-sm" data-testid="se-transport" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-gray-600">Notes</Label>
                <Input value={seNotes} onChange={e => setSeNotes(e.target.value)} placeholder="Entry notes..." className="h-9 text-sm" data-testid="se-notes" />
              </div>
            </div>

            {/* Distribution: Current packing indicator */}
            {selectedDistributor && (
              <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 text-sm">
                    <Badge className="bg-amber-500 text-white">Box {currentBoxNumber}</Badge>
                    {currentDistCustId ? (
                      <span>Packing for: <strong className="text-amber-800">{currentDistCustName}</strong></span>
                    ) : (
                      <span className="text-amber-600">Select a customer to start packing</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {rows.filter(r => r.product_id).length} items packed
                  </div>
                </div>

                {/* Customer picker for distribution */}
                {(needsCustPick || !currentDistCustId) && selectedDistributor && (
                  <div className="mt-3 relative" ref={distCustRef}>
                    <Input value={distCustSearch}
                      onChange={e => { setDistCustSearch(e.target.value); setDistCustDropdownOpen(!!e.target.value.trim()); }}
                      placeholder="Type customer name to search..." className="h-9 text-sm" autoFocus data-testid="se-dist-cust-search"
                    />
                    {distCustDropdownOpen && filteredDistCusts.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {filteredDistCusts.map(c => (
                          <div key={c.id} className="px-3 py-2 hover:bg-amber-50 cursor-pointer text-sm"
                            onClick={() => selectDistCustomer(c)} data-testid={`se-dist-cust-option-${c.id}`}>
                            <span className="font-medium">{c.company_name}</span>
                            {c.city && <span className="text-gray-400 ml-2">({c.city})</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {distCustSearch && filteredDistCusts.length === 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-sm text-gray-400">No customers found for this distributor</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default CustomerSection;
